import { CanActivateFn, Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { filter, of, switchMap } from 'rxjs';
import { InteractionStatus } from '@azure/msal-browser';
import { DestroyRef, inject } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '@workspace-app/auth/auth.service';
import { GlobalStateActions, selectIsOnline } from '@workspace-app/state/global';
import { OfflineStatePersistenceService } from '@workspace-app/shared/indexed-db/offline-state-persistence.service';

let ttlCleanupDone = false;

export const CanActivateGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const msalService = inject(MsalService);
  const msalBroadcastService = inject(MsalBroadcastService);
  const router = inject(Router);
  const store = inject(Store);
  const destroyRef = inject(DestroyRef);
  const offlinePersistence = inject(OfflineStatePersistenceService);

  return store.pipe(
    takeUntilDestroyed(destroyRef),
    select(selectIsOnline),
    switchMap((isOnline) => {
      // Load grid states synchronously (localStorage) BEFORE any routed component is built.
      // AG Grid only reads [initialState] once at creation, so the states must be in the
      // store before the list pages construct their grids — they cannot be applied later.
      store.dispatch(GlobalStateActions.loadGridStates());

      // Only load data when online to avoid unnecessary failed requests
      if (isOnline) {
        // Load all initial data in parallel for better performance
        store.dispatch(GlobalStateActions.loadInitialData());

        // Fire-and-forget TTL cleanup once per session
        if (!ttlCleanupDone) {
          ttlCleanupDone = true;
          offlinePersistence.cleanupExpiredData().subscribe();
        }
      }

      return msalBroadcastService.inProgress$.pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        switchMap(() => {
          if (!isOnline) {
            // Offline the profile cannot be fetched (MSAL cannot acquire a token without network),
            // so restore the last resolved one from localStorage instead - otherwise state.user
            // stays empty and every authorization-gated view is stuck on "Loading authorization
            // data...". Dispatched here rather than above so MSAL has finished startup and its
            // account cache is readable; the effect fails closed when no account matches.
            store.dispatch(GlobalStateActions.loadUserFromCache());
            return of(true);
          }

          if (msalService.instance.getAllAccounts().length > 0) {
            return of(true);
          }

          authService.redirectUrl = state.url;
          router.navigate(['/auth/login']); //.catch(console.error);

          return of(false);
        })
      );
    })
  );
};
